import { useEffect, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  FolderInput,
  FolderPlus,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TableCell, TableRow } from "@/components/ui/table";
import { OddelovacMenu, PolozkaMenu } from "@/components/zakazka/MenuRiadku";
import { useZamokTabulky } from "@/components/zakazka/zamokTabulky";
import type { Celok, Zostava } from "@/api/schemas";
import { cenaCelku } from "@/lib/celky";
import { formatEur, sklonuj } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * Celky zostáv v tabuľke (KOLO 235) — Kuchyňa, Obývačka nad zostavami.
 *
 * User: *„daj mi možnosť ako keby ďalší nadriadený riadok, ale to len na
 * vizuálne oddelenie zostáv… keď to pomenujeme Miestnosť 1, tak sa aj
 * v cenovej ponuke vypíše Miestnosť 1 a pod ňou dané zostavy"*. Pás celku
 * je **svetlý** (zostava je tmavá) — nadpis, nie ďalšia zostava.
 */

function popisZostav(n: number): string {
  return `${n} ${sklonuj(n, ["zostava", "zostavy", "zostáv"])}`;
}

export interface CelokRiadokProps {
  celok: Celok;
  zostavy: Zostava[];
  zbaleny: boolean;
  onToggle: () => void;
  onPremenuj: (nazov: string) => void;
  onPridajZostavu?: () => void;
  /** `null` = posun tým smerom nejde (celok je prvý / posledný). */
  onPosun: { hore: (() => void) | null; dole: (() => void) | null };
  onZrus: () => void;
  /** Pás zostavy pustený na nadpis — zostava ide na koniec celku. */
  tahanie?: { mime: string; onPustenie: (zostavaId: string) => void };
}

export function CelokRiadok({
  celok,
  zostavy,
  zbaleny,
  onToggle,
  onPremenuj,
  onPridajZostavu,
  onPosun,
  onZrus,
  tahanie,
}: CelokRiadokProps): JSX.Element {
  // Zámok obchodníka (KOLO 231) — celok je štruktúra: bez premenovania
  // a ťahania; položky menu vypne `PolozkaMenu`, zbaliť sa dá
  const zamok = useZamokTabulky();
  const [upravuje, setUpravuje] = useState(false);
  const [nazov, setNazov] = useState(celok.nazov);
  const [ciel, setCiel] = useState(false);
  const vstup = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!upravuje) setNazov(celok.nazov);
  }, [celok.nazov, upravuje]);
  useEffect(() => {
    if (upravuje) vstup.current?.select();
  }, [upravuje]);

  function uloz(): void {
    setUpravuje(false);
    const cisty = nazov.trim();
    if (cisty && cisty !== celok.nazov) onPremenuj(cisty);
    else setNazov(celok.nazov);
  }

  const nesie = (e: React.DragEvent): boolean =>
    !!tahanie && !zamok && e.dataTransfer.types.includes(tahanie.mime);

  return (
    <TableRow
      data-testid={`celok-row-${celok.id}`}
      data-ciel={ciel ? "do" : undefined}
      className={cn(
        "bg-tab-hlavicka text-tab-hlavicka-text hover:bg-tab-hlavicka",
        // Viac vzduchu ako nad zostavou (10 px) — celok je vyššia úroveň
        "!border-t-[18px] border-t-background first:!border-t-0",
        ciel && "outline outline-2 -outline-offset-2 outline-vyber",
      )}
      onDragOver={(e) => {
        if (!nesie(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setCiel(true);
      }}
      onDragLeave={() => setCiel(false)}
      onDrop={(e) => {
        if (!nesie(e) || !tahanie) return;
        e.preventDefault();
        setCiel(false);
        const id = e.dataTransfer.getData(tahanie.mime);
        if (id) tahanie.onPustenie(id);
      }}
    >
      <TableCell colSpan={15} className="border-b-2 border-b-tab-hlavicka-text px-2 py-1.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onToggle}
            aria-label={zbaleny ? `Rozbaliť celok ${celok.nazov}` : `Zbaliť celok ${celok.nazov}`}
            title="Zbaliť / rozbaliť celok"
            className="grid h-6 w-6 place-items-center rounded hover:bg-black/5"
          >
            {zbaleny ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {upravuje ? (
            <Input
              ref={vstup}
              value={nazov}
              aria-label="Názov celku"
              maxLength={100}
              onChange={(e) => setNazov(e.target.value)}
              onBlur={uloz}
              onKeyDown={(e) => {
                if (e.key === "Enter") uloz();
                if (e.key === "Escape") {
                  setNazov(celok.nazov);
                  setUpravuje(false);
                }
              }}
              className="h-7 w-64 text-sm font-semibold"
            />
          ) : (
            <button
              type="button"
              onDoubleClick={() => {
                if (!zamok) setUpravuje(true);
              }}
              title={zamok ?? "Dvojklik — premenovať"}
              className="truncate text-left font-display text-sm font-semibold uppercase tracking-wide"
            >
              {celok.nazov}
            </button>
          )}
          <span className="text-xs text-tab-hlavicka-text/80">
            {zostavy.length === 0 ? "prázdny — potiahni sem zostavu" : popisZostav(zostavy.length)}
          </span>
          <span className="flex-1" />
          {zostavy.length > 0 ? (
            <span className="whitespace-nowrap font-mono text-xs font-semibold" title="Súčet cien zostáv celku bez DPH">
              {formatEur(cenaCelku(zostavy))}
            </span>
          ) : null}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Menu celku ${celok.nazov}`}
                className="grid h-6 w-6 place-items-center rounded hover:bg-black/5"
              >
                <MoreVertical className="h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 p-1">
              <PolozkaMenu ikona={Pencil} onSelect={() => setUpravuje(true)}>
                Premenovať celok
              </PolozkaMenu>
              {onPridajZostavu ? (
                <PolozkaMenu ikona={Plus} onSelect={onPridajZostavu}>
                  Pridať zostavu do celku
                </PolozkaMenu>
              ) : null}
              <PolozkaMenu
                ikona={ArrowUp}
                disabled={!onPosun.hore}
                dovod="celok je prvý"
                onSelect={() => onPosun.hore?.()}
              >
                Posunúť celok hore
              </PolozkaMenu>
              <PolozkaMenu
                ikona={ArrowDown}
                disabled={!onPosun.dole}
                dovod="celok je posledný"
                onSelect={() => onPosun.dole?.()}
              >
                Posunúť celok dole
              </PolozkaMenu>
              <OddelovacMenu />
              <PolozkaMenu ikona={Trash2} nebezpecna onSelect={onZrus}>
                Zrušiť celok
              </PolozkaMenu>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * Nový celok — názov a voliteľne vybrané zostavy rovno doň.
 *
 * `zostavy` sú tie, ktoré sa dajú doň dať (výber v tabuľke alebo zostava,
 * z ktorej menu prišlo); prázdny celok sa dá založiť tiež.
 */
export function NovyCelokDialog({
  zostavy,
  onClose,
  onVytvor,
  ukladam = false,
}: {
  zostavy: Zostava[];
  onClose: () => void;
  onVytvor: (nazov: string, zostavaIds: string[]) => void;
  ukladam?: boolean;
}): JSX.Element {
  const [nazov, setNazov] = useState("");
  const [sZostavami, setSZostavami] = useState(zostavy.length > 0);
  const cisty = nazov.trim();

  function potvrd(): void {
    if (!cisty || ukladam) return;
    onVytvor(cisty, sZostavami ? zostavy.map((z) => z.id) : []);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Nový celok</DialogTitle>
          <DialogDescription>
            Nadpis nad zostavami — napr. Kuchyňa alebo Miestnosť 1. V cenovej ponuke
            bude nad svojimi zostavami so súčtom „… spolu“.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="novy-celok-nazov">Názov</Label>
            <Input
              id="novy-celok-nazov"
              autoFocus
              value={nazov}
              maxLength={100}
              placeholder="Kuchyňa"
              onChange={(e) => setNazov(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") potvrd();
              }}
            />
          </div>
          {zostavy.length > 0 ? (
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-primary"
                checked={sZostavami}
                onChange={(e) => setSZostavami(e.target.checked)}
                aria-label="Dať do celku vybrané zostavy"
              />
              <span>
                Dať doň {zostavy.length === 1 ? "zostavu" : "zostavy"}{" "}
                <b>{zostavy.map((z) => z.kod).join(", ")}</b>
              </span>
            </label>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Zrušiť
          </Button>
          <Button onClick={potvrd} disabled={!cisty || ukladam}>
            Vytvoriť celok
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Podmenu „Dať do celku ▸" v menu pásu zostavy — celky, Bez celku
 * a Nový celok…; aktuálny celok je so značkou a nedá sa vybrať.
 */
export function PodmenuDoCelku({
  celky,
  aktualny,
  onVyber,
  onNovy,
}: {
  celky: Celok[];
  /** Celok zostavy; pri viacerých zostavách s rôznymi celkami `undefined`. */
  aktualny: string | null | undefined;
  onVyber: (celokId: string | null) => void;
  onNovy: () => void;
}): JSX.Element {
  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger className="gap-2 py-1 text-[13px]">
        <FolderInput className="h-3.5 w-3.5 shrink-0" />
        <span className="flex-1">Dať do celku</span>
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent className="w-52 p-1">
        {celky.map((c) => (
          <PolozkaMenu
            key={c.id}
            ikona={aktualny === c.id ? Check : FolderInput}
            disabled={aktualny === c.id}
            onSelect={() => onVyber(c.id)}
          >
            {c.nazov}
          </PolozkaMenu>
        ))}
        {celky.length > 0 ? <OddelovacMenu /> : null}
        <PolozkaMenu ikona={FolderPlus} onSelect={onNovy}>
          Nový celok…
        </PolozkaMenu>
        {aktualny !== null ? (
          <PolozkaMenu ikona={FolderInput} onSelect={() => onVyber(null)}>
            Bez celku
          </PolozkaMenu>
        ) : null}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
